import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as Toast from "@radix-ui/react-toast";
import * as Tooltip from "@radix-ui/react-tooltip";
import { relativeTime } from "../shared/format";
import type { SeriesPoint } from "../shared/types";
import { AlertToast } from "./components/AlertToast";
import { Burnline } from "./components/Burnline";
import { BurnSankey } from "./components/BurnSankey";
import { Burnstream } from "./components/Burnstream";
import { createBlockPresentationChannel } from "./components/blockPresentation";
import { InflationDestinations } from "./components/Breakdowns";
import { ContextPanels } from "./components/ContextPanels";
import { ExperienceCanvas } from "./components/ExperienceCanvas";
import { FlameIcon, HealthIcon, WarningIcon } from "./components/Icons";
import { Methodology } from "./components/Methodology";
import { ValidatorFooter } from "./components/ValidatorFooter";
import { liveStore, mergeSessionBlocks, useLiveDashboard } from "./store/liveStore";

const SupplyChart = lazy(() => import("./components/SupplyChart").then((module) => ({ default: module.SupplyChart })));

const DeferredSupplyChart=memo(function DeferredSupplyChart({ points }: { points: SeriesPoint[] }) {
  const boundary = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  useEffect(() => {
    if (!boundary.current) return;
    if (typeof IntersectionObserver === "undefined") {
      const timer = window.setTimeout(() => setNearViewport(true), 0);
      return () => window.clearTimeout(timer);
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: "480px 0px" });
    observer.observe(boundary.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={boundary}>
      {nearViewport
        ? <Suspense fallback={<div className="supply-chart__canvas chart-loading" aria-busy="true">Preparing supply flow…</div>}><SupplyChart points={points} /></Suspense>
        : <div className="supply-chart__canvas chart-loading" aria-hidden="true">Supply flow loads as it approaches.</div>}
    </div>
  );
});

function Skeleton() {
  return <main className="loading-shell" aria-busy="true"><div className="loading-line"/><div className="loading-stage"/><p>Loading verified tokenomics…</p></main>;
}

export default function App() {
  const { dashboard, loading, refreshing, error, lastAlert, connection } = useLiveDashboard();
  const blockPresentation=useMemo(()=>createBlockPresentationChannel(),[]);
  useEffect(() => {
    document.documentElement.dataset.severity = lastAlert?.severity ?? "routine";
  }, [lastAlert]);

  if (loading && !dashboard) return <Skeleton />;
  if (!dashboard) {
    return <main className="fatal-state"><FlameIcon/><h1>Tokenomics data is unavailable</h1><p>{error ?? "The service is recovering automatically."}</p><button onClick={() => liveStore.refresh()}>Try again</button></main>;
  }

  const { summary, status } = dashboard;
  const burnstreamBlocks = mergeSessionBlocks([], dashboard.liveBlocks, 60);
  return (
    <Tooltip.Provider delayDuration={260}>
      <Toast.Provider swipeDirection="right">
        <ExperienceCanvas>
        <header className="topbar">
          <a className="wordmark" href="#top" aria-label="Flare Burn Monitor home"><img src="/flare-burn-monitor-logo.png" alt=""/><span>FLARE <b>BURN</b> <span className="wordmark__monitor">MONITOR</span></span></a>
          <div className="topbar__status">
            <span className={`live-dot live-dot--${connection}`} />
            <span>{connection === "live" ? "Live" : connection}</span>
          </div>
          <nav className="topbar__nav" aria-label="Page sections">
            <a href="#burnstream">Live feed</a>
            <a className="topbar__method" href="#methodology">Method</a>
          </nav>
          <span className="topbar__progress" aria-hidden="true"><i /></span>
        </header>

        <main id="top">
          {!status.coverageComplete && (
            <aside className={`data-banner data-banner--${status.source === "demo" ? "preview" : "warning"}`} role="status">
              {status.source === "demo" ? <HealthIcon /> : <WarningIcon />}
              <b>{status.source === "demo" ? "Preview data" : "Coverage incomplete"}</b>
              <span>{status.message}</span>
            </aside>
          )}

          <div className="dashboard-shell">
            <Burnline summary={summary} presentation={blockPresentation} />

            <Burnstream blocks={burnstreamBlocks} price={summary.price} connection={connection} onBlockPresented={blockPresentation.publish}/>

            <div className="analysis-window" aria-label="Analysis window: rolling 30 days">
              <div><span className="eyebrow">Analysis window</span><strong>Rolling 30 days</strong></div>
              <span aria-live="polite">{refreshing ? "Refreshing verified data…" : `Updated ${relativeTime(summary.updatedAt)}`}</span>
            </div>

            {error && <div className="inline-error" role="status"><b>Refresh delayed.</b> {error} Existing verified data remains visible.</div>}

            <div className="primary-grid">
              <BurnSankey origins={dashboard.origins} totalBurnWei={summary.irreversiblyBurnedWei} presentation={blockPresentation}/>
              <section className="chart-panel" aria-labelledby="supply-flow-title">
                <div className="section-heading"><div><span className="eyebrow">Daily observations · inflation above · deflation below</span><h2 id="supply-flow-title">Supply Flow</h2></div><span className="chart-unit">FLR per day</span></div>
                <DeferredSupplyChart points={dashboard.series} />
              </section>
            </div>

            <div className="breakdown-grid breakdown-grid--single">
              <InflationDestinations rows={dashboard.inflation} />
            </div>

            <ContextPanels fire={dashboard.fire} />

            <Methodology />
          </div>
        </main>

        <AlertToast key={lastAlert?.sequence ?? "no-alert"} block={lastAlert} />
        <Toast.Viewport className="toast-viewport" hotkey={["F8"]} label="Burn notifications ({hotkey})" />
        <ValidatorFooter />
        </ExperienceCanvas>
      </Toast.Provider>
    </Tooltip.Provider>
  );
}
